import posthog from 'posthog-js'

import { UseQueryOptions, UseQueryResult, useQuery, useQueryClient } from '@tanstack/react-query'

import { CHAIN_IDS, ChainName, isTestnet } from '@src/configs/chainInfo'
import { CACHE_HALF_FULL_URL, CACHE_HALF_FULL_URL_TESTNET } from '@src/configs/env'
import { ResultCode } from '@src/enums'
import { cacheHalfFullApi, cacheHalfFullTestnetApi } from '@src/libs/cacheHalfFull'
import { IOrderHistoryItem, IQuoteData, IQuoteParams, IQuoteResponse } from '@src/types/order'
import { isFeatureEnabled } from '@src/utils'
import { getChecksumAddress } from '@src/utils/address'
import { createContextLogger } from '@src/utils/logger'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'

const logger = createContextLogger('order.api')

export const apiGetOrderHistory = async (address: string): Promise<IOrderHistoryItem[]> => {
    const endpoint = `/v1/orders?wallet=${address}`
    const apiCalls = [cacheHalfFullApi.get<IOrderHistoryItem[]>(endpoint)]

    if (isFeatureEnabled('USE_TESTNETS_ONLY') || isFeatureEnabled('ENABLE_TESTNETS')) {
        apiCalls.push(cacheHalfFullTestnetApi.get<IOrderHistoryItem[]>(endpoint))
    }

    const [mainnetResult, testnetResult] = await Promise.allSettled(apiCalls)

    let orders: IOrderHistoryItem[] = []

    // Process mainnet result.
    if (mainnetResult && mainnetResult.status === 'fulfilled') {
        orders = orders.concat(mainnetResult.value.data)
    } else {
        logger.error(`[apiGetOrderHistory] Mainnet API failed.`)
    }

    // Process testnet result.
    if (testnetResult && testnetResult.status === 'fulfilled') {
        orders = orders.concat(testnetResult.value.data)
    } else {
        logger.error(`[apiGetOrderHistory] Testnet API failed.`)
    }

    // Deduplicate orders based on a unique identifier (e.g., order ID)
    const uniqueOrders = Array.from(new Map(orders.map(order => [order.id, order])).values())

    return uniqueOrders
}

export const apiSendOrderData = async (orderData: any, referralCode?: string, referrer?: string) => {
    if (orderData === undefined) {
        logger.error('Invalid order data received', { orderData })
        posthog.capture('api_send_order_failed', {
            error: 'Error decoding order data',
            error_type: 'InvalidInput',
        })
        return { message: 'Error decoding order data', status: ResultCode.failure }
    }

    // Remove space from sellChain
    const sellChain = orderData.sellChain.replace(/\s+/g, '')
    const payload = {
        chain: sellChain,
        place_taker_tx: orderData.transactionHash,
        referral_code: referralCode,
        referrer,
    }

    logger.info('Sending order data', { payload })
    let response

    try {
        let api = null

        if (!isTestnet(CHAIN_IDS[payload.chain as ChainName])) {
            logger.info(
                `[apiSendOrderData] Using mainnet API (url = ${CACHE_HALF_FULL_URL})\npayload.chain = ${payload.chain}`
            )
            api = cacheHalfFullApi
        } else {
            logger.info(
                `[apiSendOrderData] Using testnet API (url = ${CACHE_HALF_FULL_URL_TESTNET})\npayload.chain = ${payload.chain}`
            )
            api = cacheHalfFullTestnetApi
        }

        response = await api.post('/v1/orders', payload)
        logger.info('Order API response received', { status: response?.status })

        switch (Number(response.status)) {
            case 200:
                logger.info('Order successfully sent', {
                    orderId: response.data.id,
                    chain: orderData.sellChain,
                })
                posthog.capture('api_send_order_success', {
                    chain: orderData.sellChain,
                    has_referral: !!referralCode,
                    status: 200,
                })
                return {
                    message: 'Order successfully sent.',
                    status: ResultCode.pending,
                    eta: response.data.eta,
                    id: response.data.id,
                }
            case 202:
                logger.info('Order accepted with delay', {
                    chain: orderData.sellChain,
                    eta: response.data.eta,
                })
                posthog.capture('api_send_order_success', {
                    chain: orderData.sellChain,
                    has_referral: !!referralCode,
                    status: 202,
                })
                return {
                    message: 'Order accepted but will take 5-20 minutes to find a match.',
                    status: ResultCode.pending,
                    eta: response.data.eta,
                }
            case 400:
                logger.warn('Order rejected by market maker', {
                    chain: orderData.sellChain,
                    error: response.data,
                })
                posthog.capture('api_send_order_failed', {
                    chain: orderData.sellChain,
                    error_type: 'MarketMakerRejection',
                    error_data: response.data,
                })
                return {
                    message: 'Order will not be filled by the market maker at this time.',
                    status: ResultCode.failure,
                    errorobj: response.data,
                }
            case 422:
                logger.error('Invalid payload sent to API', {
                    chain: orderData.sellChain,
                    error: response.data,
                    payload,
                })
                posthog.capture('api_send_order_failed', {
                    chain: orderData.sellChain,
                    error_type: 'InvalidPayload',
                    error_data: response.data,
                })
                return {
                    message: 'Error in the payload.',
                    status: ResultCode.failure,
                    errorobj: response.data,
                }
            default:
                logger.error('Unexpected API response', {
                    status: response.status,
                    chain: orderData.sellChain,
                    error: response.data,
                })
                posthog.capture('api_send_order_failed', {
                    chain: orderData.sellChain,
                    error_type: 'UnexpectedStatus',
                    status: response.status,
                    error_data: response.data,
                })
                return {
                    message: 'Unexpected error occurred.',
                    status: ResultCode.failure,
                    errorobj: response.data,
                }
        }
    } catch (error) {
        logger.error('[apiSendOrderData] Failed to send order', {
            error: error instanceof Error ? error.message : String(error),
            chain: orderData.sellChain,
        })
        posthog.capture('api_send_order_failed', {
            chain: orderData.sellChain,
            error_type: 'NetworkError',
            error_message: response?.data?.message || 'An unspecified error occurred.',
            error_object: error instanceof Error ? error.message : String(error),
        })
        return {
            message: response?.data?.message || 'An unspecified error occurred.',
            status: ResultCode.failure,
            errorobj: error,
            response: response,
        }
    }
}

export const getQuote = async (
    { walletAddress, srcChain, dstChain, srcAssetAddress, dstAssetAddress, srcAmount, targetAddress }: IQuoteParams,
    signal?: AbortSignal
): Promise<IQuoteResponse> => {
    // Add quote request tracking
    const quoteStartTime = performance.now()

    // Track the quote request
    posthog.capture('quote_requested', {
        src_chain: srcChain.toLowerCase(),
        dst_chain: dstChain.toLowerCase(),
        src_asset_address: srcAssetAddress,
        dst_asset_address: dstAssetAddress,
        src_amount: srcAmount.toString(),
        wallet_address: walletAddress,
        has_target_address: !!targetAddress,
    })

    let payload
    if (!!targetAddress) {
        payload = {
            wallet_address: getChecksumAddress(walletAddress),
            src_chain: srcChain.toLowerCase(),
            dst_chain: dstChain.toLowerCase(),
            src_asset_address: getChecksumAddress(mapTokenAddress(srcAssetAddress, true, srcChain as ChainName)),
            dst_asset_address: getChecksumAddress(mapTokenAddress(dstAssetAddress, true, dstChain as ChainName)),
            src_amount: `${srcAmount}`,
            target_address: targetAddress,
        }
    } else {
        payload = {
            wallet_address: getChecksumAddress(walletAddress),
            src_chain: srcChain.toLowerCase(),
            dst_chain: dstChain.toLowerCase(),
            src_asset_address: getChecksumAddress(mapTokenAddress(srcAssetAddress, true, srcChain as ChainName)),
            dst_asset_address: getChecksumAddress(mapTokenAddress(dstAssetAddress, true, dstChain as ChainName)),
            src_amount: `${srcAmount}`,
        }
    }

    logger.info('[getQuote] Quote requested', payload)

    try {
        let api = null

        if (!isTestnet(CHAIN_IDS[srcChain]) && !isTestnet(CHAIN_IDS[dstChain])) {
            logger.info(`[getQuote] Using mainnet API (url = ${CACHE_HALF_FULL_URL})`)
            api = cacheHalfFullApi
        } else if (isTestnet(CHAIN_IDS[srcChain]) && isTestnet(CHAIN_IDS[dstChain])) {
            logger.info(`[getQuote] Using testnet API (url = ${CACHE_HALF_FULL_URL_TESTNET})`)
            api = cacheHalfFullTestnetApi
        } else {
            logger.error(`Unable to fetch testnet <-> mainnet quote.\n(srcChain=${srcChain} dstChain=${dstChain})`)
            throw new Error('Unable to fetch testnet <-> mainnet quote.')
        }

        const response = await api.post<any>('/v1/quotes', payload, {
            signal,
        })

        logger.info('Quote response received', { response })

        switch (response.status) {
            case 200: {
                // Extract the numeric values using regex to avoid any parsing
                const extractNumber = (field: string) => {
                    // Regex to match both quoted ("1234") and unquoted (1234) numbers
                    // e.g. consume both src_amount:1000 and src_amount:"1000"
                    const regexstr = `"${field}":\\"?([0-9]+)\\"?` // Use \\"? to match optional quotes around the number
                    const regex = new RegExp(regexstr)
                    const match = response.request.response.match(regex)
                    return match ? BigInt(match[1]) : null
                }

                type NumericFields = {
                    src_amount: bigint | null
                    dst_amount: bigint | null
                    bond_amount: bigint | null
                    bond_fee: bigint | null
                }

                const rawNetworkResponse: NumericFields = {
                    src_amount: extractNumber('src_amount'),
                    dst_amount: extractNumber('dst_amount'),
                    bond_amount: extractNumber('bond_amount'),
                    bond_fee: extractNumber('bond_fee'),
                }

                const missingFields: string[] = []

                // Check for missing fields and log details
                if (rawNetworkResponse.src_amount === null || rawNetworkResponse.src_amount === undefined) {
                    missingFields.push(`src_amount (expected a numeric value, got ${rawNetworkResponse.src_amount})`)
                }
                if (rawNetworkResponse.dst_amount === null || rawNetworkResponse.dst_amount === undefined) {
                    missingFields.push(`dst_amount (expected a numeric value, got ${rawNetworkResponse.dst_amount})`)
                }
                if (rawNetworkResponse.bond_amount === null || rawNetworkResponse.bond_amount === undefined) {
                    missingFields.push(`bond_amount (expected a numeric value, got ${rawNetworkResponse.bond_amount})`)
                }

                // Return error if any fields are missing
                if (missingFields.length > 0) {
                    posthog.capture('quote_failed', {
                        error_type: 'MissingFields',
                        missing_fields: missingFields,
                        src_chain: srcChain,
                        dst_chain: dstChain,
                    })
                    return {
                        status: ResultCode.failure,
                        errorMsg: `Missing required numeric values in response: ${missingFields.join(', ')}`,
                        quote: null,
                    }
                } else {
                    const quoteData = {
                        ...response.data,
                        src_amount: rawNetworkResponse.src_amount ?? BigInt(0), // actually will never be zero, just put there to silence compilation error.
                        dst_amount: rawNetworkResponse.dst_amount ?? BigInt(0), // actually will never be zero, just put there to silence compilation error.
                        bond_amount: rawNetworkResponse.bond_amount ?? BigInt(0), // actually will never be zero, just put there to silence compilation error.
                    }

                    logger.info('Quote received', { quoteData })

                    if (quoteData.liquidity_source && quoteData.liquidity_source == 'unavailable') {
                        posthog.capture('quote_failed', {
                            error_type: 'LiquidityUnavailable',
                            src_chain: srcChain,
                            dst_chain: dstChain,
                            src_asset: srcAssetAddress,
                            dst_asset: dstAssetAddress,
                        })
                        return {
                            status: ResultCode.failure,
                            quote: quoteData as IQuoteData,
                            errorMsg: 'Liquidity source unavailable.',
                        }
                    }

                    posthog.capture('quote_success', {
                        src_chain: srcChain,
                        dst_chain: dstChain,
                        src_asset_address: srcAssetAddress,
                        dst_asset_address: dstAssetAddress,
                        src_amount: srcAmount.toString(),
                        dst_amount: quoteData.dst_amount.toString(),
                        bond_amount: quoteData.bond_amount.toString(),
                        quote_time_ms: performance.now() - quoteStartTime,
                        has_target_address: !!targetAddress,
                        liquidity_source: quoteData.liquidity_source,
                    })
                    return {
                        status: ResultCode.success,
                        quote: quoteData as IQuoteData,
                    }
                }
            }

            case 400:
            case 500:
                posthog.capture('quote_failed', {
                    error_type: 'ApiError',
                    status: response.status,
                    error_message: response.statusText,
                    src_chain: srcChain,
                    dst_chain: dstChain,
                })
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: response.statusText,
                }

            default:
                posthog.capture('quote_failed', {
                    error_type: 'UnexpectedStatus',
                    status: response.status,
                    src_chain: srcChain,
                    dst_chain: dstChain,
                })
                return {
                    status: ResultCode.failure,
                    quote: null,
                }
        }
    } catch (error) {
        posthog.capture('quote_failed', {
            error_type: 'NetworkError',
            error_message: error instanceof Error ? error.message : 'Unknown error',
            src_chain: srcChain,
            dst_chain: dstChain,
        })
        return {
            errorMsg: error instanceof Error ? error.message : 'An unknown error occurred',
            status: ResultCode.failure,
            quote: null,
        }
    }
}

/**
 * Hook to fetch a quote based on provided parameters.
 *
 * This hook uses TanStack Query to perform a network request to fetch a quote.
 * It automatically handles errors, cancellation, and provides helper methods for
 * invalidating and refetching the query or forcefully canceling an ongoing request.
 *
 * @param {IQuoteParams | undefined} params - The parameters required to fetch a quote.
 *   Must be defined and have a non-zero `srcAmount`.
 * @param {Omit<UseQueryOptions<IQuoteResponse>, 'queryKey' | 'queryFn'>} [options] -
 *   Additional options for configuring the query.
 *
 * @returns {UseQueryResult<IQuoteResponse> & {
 *   data: IQuoteResponse,
 *   invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<void>,
 *   forceCancel: () => Promise<void>
 * }} An object containing the query result and the following helper functions:
 *
 * - **invalidateQuoteAndRefetch:** Invalidates the current quote query and prefetches a new one with updated parameters.
 * - **forceCancel:** Forcefully cancels an ongoing quote query.
 */
export const useQuote = (
    params: IQuoteParams | undefined,
    isQuoteEnabled: boolean,
    options?: Omit<UseQueryOptions<IQuoteResponse>, 'queryKey' | 'queryFn'>
) => {
    const isEnabled = !!params && params.srcAmount !== BigInt(0) && isQuoteEnabled
    const queryClient = useQueryClient()

    const query = useQuery<IQuoteResponse>({
        queryKey: ['quote', JSON.stringify(params)],
        queryFn: async ({ signal }) => {
            logger.info('[useQuote] queryKey=', ['quote', JSON.stringify(params)], 'params=', { params })

            if (!isQuoteEnabled) {
                logger.info('[useQuote] Aborting query because isQuoteEnabled is false')
                throw new Error(`isQuoteEnabled is false`)
            } else {
                // Just to debug.
                logger.info('[useQuote] isQuoteEnabled=true, continuing...')
            }

            if (!params) {
                logger.error('Fetch initiated without params')
                // Throwing here to prevent an indefinitely loading query.
                throw new Error(`Quote parameters are required`)
            }

            if (params.srcAmount === BigInt(0)) {
                logger.error('[useQuote] srcAmount is 0')
                // Throwing here to prevent an indefinitely loading query.
                throw new Error(`Quote srcAmount is 0.`)
            }

            try {
                const result = await getQuote(params, signal)

                if (result.status === ResultCode.failure) {
                    logger.error(
                        `[useQuote] An error encountered while fetching quote: ${result.errorMsg ?? 'Unknown error.'}`
                    )
                }
                return result
            } catch (error) {
                // Log if the error is due to query cancellation.
                if (error instanceof Error && error.name === 'AbortError') {
                    logger.info('[useQuote] Query was forcefully cancelled.')
                }
                const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
                return {
                    status: ResultCode.failure,
                    quote: null,
                    errorMsg: errorMessage,
                }
            }
        },
        enabled: isEnabled,
        refetchOnMount: true,
        notifyOnChangeProps: ['data', 'error', 'isLoading', 'isFetching'],
        gcTime: 1000,
        initialData: {
            status: ResultCode.pending,
            quote: null,
            errorMsg: 'Uninitialized query call.',
        },
        ...options,
    })

    /**
     * Forcefully cancels the ongoing quote query.
     *
     * This function can be used to immediately abort a running query—such as in response to a user action or some other event.
     *
     * @returns {Promise<void>} A promise that resolves once the query has been cancelled.
     */
    const forceCancel = async (): Promise<void> => {
        logger.info('[useQuote] Force cancelling the quote query')
        await queryClient.cancelQueries({
            queryKey: ['quote'],
        })
    }

    /**
     * Invalidates the current quote query and prefetches a new one with updated parameters.
     *
     * Use this function when you need to refresh the quote with new parameters.
     *
     * @param {IQuoteParams} newParams - The new parameters for fetching the quote.
     * @returns {Promise<void>} A promise that resolves once the new quote query has been prefetched.
     */
    const invalidateQuoteAndRefetch = async (newParams: IQuoteParams): Promise<void> => {
        logger.info('[useQuote] Invalidating and refetching', { newParams })
        if (!isQuoteEnabled) {
            logger.info('[useQuote] Aborting invalidateQuoteAndRefetch because isQuoteEnabled is false')
            return
        }

        // Invalidate the current query
        await queryClient.invalidateQueries({ queryKey: ['quote'] })

        // Optionally clear stale data
        queryClient.setQueryData(['quote', JSON.stringify(newParams)], undefined)

        // Prefetch the new quote query
        await queryClient.prefetchQuery({
            queryKey: ['quote', JSON.stringify(newParams)],
            queryFn: async () => await getQuote(newParams),
        })
    }

    // Return the query object along with helper functions.
    return {
        ...query,
        invalidateQuoteAndRefetch,
        forceCancel,
        // Ensure `data` is always of type `IQuoteResponse`
        data: query.data || {
            status: ResultCode.failure,
            quote: null,
            errorMsg: '[useQuote] Uninitialized query call.',
        },
    } as UseQueryResult<IQuoteResponse> & {
        data: IQuoteResponse
        invalidateQuoteAndRefetch: (newParams: IQuoteParams) => Promise<void>
        forceCancel: () => Promise<void>
    }
}

export const apiNewPostReceiveCctpMessage = async (
    srcChain: string,
    burnTx: string,
    solana_token_owner_address?: string
) => {
    logger.info('[useQuote] apiNewPostReceiveCctpMessage called')
    try {
        const payload: Record<string, any> = {
            chain: srcChain.toLowerCase(),
            burn_tx: burnTx,
        }
        if (solana_token_owner_address) {
            payload.solana_token_owner_address = solana_token_owner_address
        }
        const response = await cacheHalfFullApi.post('/v1/orders/cctp', payload)
        if (response.status == 200 || response.status === 202) {
            const result = {
                message: 'Order accepted but will take 5-20 minutes to find a match.',
                status: ResultCode.pending,
                eta: response.data.eta,
                id: response.data.id,
            }
            return result
        }
    } catch (error) {
        logger.error('[useQuote] apiNewPostReceiveCctpMessage error')
        posthog.capture('[useQuote] apiNewPostReceiveCctpMessage Failure', {
            srcChain: srcChain,
            burnTx: burnTx,
            solana_token_owner_address: solana_token_owner_address,
        })
        return {
            message: 'Order will not be filled by the market maker at this time.',
            error: error,
            status: ResultCode.failure,
            eta: 0,
            id: null,
        }
    }
}
